import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import TabNav from "../../../../CommonComponents/TabNav/TabNav";
import ScreenHeader from "../../../../CommonComponents/ScreenHeader";
import ChantButton from "../../../../CommonComponents/ChantButton/ChantButton";
import Chants from "./Chants";
import ChantSwitch from "../../../../CommonComponents/ChantSwitch/ChantSwitch";
import IconContainer from "../../../../CommonComponents/IconContainer";
import { PlusOutlined } from "@ant-design/icons";
import { isMobile } from "../../../../utils/helper";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../store/adminData/adminData";
import { db } from "../../../../services/firebaseService/connection";
import logger from "../../../../utils/logger";
import { refreshGroupData } from "../../../../redux/actions/adminData";
import { postUpdateCDNUtil } from "../../../../helperFunctions/util";

const AdminChants = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const [chantsCount, setChantsCount] = useState(0);
  const [showGlobalChantsSwitch, setShowGlobalChantsSwitch] = useState(false);
  const [globalChantsLoading, setGlobalChantsLoading] = useState(false);

  const getCurrentTab = () => {
    if (location.pathname.includes("/archive")) {
      return "archive";
    }
    return "current";
  };

  const menuItems = [
    {
      key: "current",
      label: "Current",
    },
    {
      key: "archive",
      label: "Archive",
    },
  ];

  const handleTabChange = (tabKey) => {
    switch (tabKey) {
      case "current":
        navigate("/admin/chants");
        break;
      case "archive":
        navigate("/admin/chants/archive");
        break;
      default:
        navigate("/admin/chants");
    }
  };

  const renderContent = () => {
    const showGlobalChants = groupData?.showGlobalChants ?? true;

    if (location.pathname.includes("/archive")) {
      return (
        <Chants
          archive={true}
          setChantsCount={setChantsCount}
          setShowGlobalChantsSwitch={setShowGlobalChantsSwitch}
          showGlobalChants={showGlobalChants}
        />
      );
    }
    return (
      <Chants
        archive={false}
        setChantsCount={setChantsCount}
        setShowGlobalChantsSwitch={setShowGlobalChantsSwitch}
        showGlobalChants={showGlobalChants}
      />
    );
  };

  const handleShowGlobalChantsChange = async () => {
    const currentValue = groupData?.showGlobalChants ?? true;
    const newValue = !currentValue;

    try {
      setGlobalChantsLoading(true);

      if (!groupData?.id) {
        logger.error("Group ID is not available");
        return;
      }

      const groupRef = doc(db, "group", groupData.id);
      const groupDoc = await getDoc(groupRef);

      if (!groupDoc.exists()) {
        logger.error("Group document does not exist");
        return;
      }

      await updateDoc(groupRef, {
        showGlobalChants: newValue,
      });

      postUpdateCDNUtil(groupData?.id);
      await refreshGroupData(groupData?.id);
    } catch (error) {
      logger.error("Error updating showGlobalChants flag:", error);
    } finally {
      setGlobalChantsLoading(false);
    }
  };

  return (
    <div className="admin-chants-container" style={{ padding: "12px" }}>
      <ScreenHeader
        elementCount={chantsCount}
        title="Chants"
        actions={
          getCurrentTab() === "current" && (
            <div style={{ display: "flex", gap: "22px" }}>
              {showGlobalChantsSwitch && (
                <ChantSwitch
                  disabled={globalChantsLoading}
                  loading={globalChantsLoading}
                  label="Show global chants?"
                  size="small"
                  checked={groupData?.showGlobalChants ?? true}
                  onChange={handleShowGlobalChantsChange}
                />
              )}
              {isMobile() ? (
                <IconContainer
                  onClick={() => navigate("/admin/chants/create")}
                  iconComponent={<PlusOutlined />}
                  iconStyle={{ fontSize: "16px" }}
                />
              ) : (
                <ChantButton onClick={() => navigate("/admin/chants/create")}>
                  Create Chant
                </ChantButton>
              )}
            </div>
          )
        }
      />

      <div style={{ paddingTop: "2px" }}>
        <TabNav
          items={menuItems}
          activeKey={getCurrentTab()}
          handleTabChange={handleTabChange}
        />
      </div>
      <div className="admin-chants-content">{renderContent()}</div>
    </div>
  );
};

export default AdminChants;
